/**
 * Generic create/edit form page backed by the authenticated experience API.
 * The browser supplies only a resource id and record id. Field layout,
 * writable properties, options, actions, validation, API bindings, and routes
 * remain backend-owned.
 */
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  FormSubmissionContext,
  FormSubmissionResult,
  ActionDescriptor,
} from '@finance/contracts';
import type { ActionExecutionContext } from '../../runtime/actions/action-executor';
import { AdminFormPage, type AdminFormSuccessPolicy } from './AdminFormPage';
import { PageForbiddenState, PageLoadingState, PageNotFoundState } from '../states';
import { ResourceLoadErrorState } from './ResourceLoadErrorState';
import { useExperience } from '../../features/experience/ExperienceProvider';
import {
  useGenericResourceRecord,
  useResolvedResource,
} from '../../features/experience/useResolvedExperience';
import {
  experienceQueryKeys,
  submitResolvedResourceForm,
} from '../../features/experience/experience-api';

export type GenericFormMode = 'create' | 'edit' | 'view';

export interface GenericResourceFormPageProps {
  resourceId: string;
  mode: GenericFormMode;
  recordId?: string;
  onSubmit?: (
    commandId: string,
    values: Record<string, unknown>,
    context: FormSubmissionContext,
  ) => Promise<FormSubmissionResult>;
  onAction?: (
    action: ActionDescriptor,
    context?: ActionExecutionContext,
  ) => void | Promise<void>;
  initialValues?: Record<string, unknown>;
  loadingInitialValues?: boolean;
  recordVersion?: number;
  successPolicy?: AdminFormSuccessPolicy;
}

export function GenericResourceFormPage({
  resourceId,
  mode,
  recordId,
  onSubmit,
  onAction,
  initialValues,
  loadingInitialValues,
  recordVersion,
  successPolicy,
}: GenericResourceFormPageProps) {
  const queryClient = useQueryClient();
  const experience = useExperience();
  const descriptorQuery = useResolvedResource(resourceId);
  const recordQuery = useGenericResourceRecord(
    resourceId,
    mode === 'edit' || mode === 'view' ? recordId : undefined,
    mode === 'edit' ? 'edit' : 'view',
  );
  const resolved = descriptorQuery.data;

  const handleSubmit = useCallback(
    async (
      commandId: string,
      values: Record<string, unknown>,
      context: FormSubmissionContext,
    ): Promise<FormSubmissionResult> => {
      if (onSubmit) return onSubmit(commandId, values, context);
      if (mode === 'view') {
        return {
          status: 'failed',
          globalErrors: [{ code: 'common.error.unexpected' as never }],
        };
      }
      if (!resolved) {
        return {
          status: 'failed',
          globalErrors: [{ code: 'common.error.unexpected' as never }],
        };
      }
      const result = await submitResolvedResourceForm(
        resolved,
        mode,
        values,
        recordId,
      );
      if (result.status === 'succeeded') {
        await queryClient.invalidateQueries({ queryKey: experienceQueryKeys.all });
      }
      return result;
    },
    [mode, onSubmit, queryClient, recordId, resolved],
  );

  const needsRecord = mode === 'edit' || mode === 'view';
  if (
    descriptorQuery.isLoading ||
    experience.loading ||
    (needsRecord && recordQuery.isLoading) ||
    loadingInitialValues
  ) {
    return <PageLoadingState />;
  }
  if (
    descriptorQuery.isError ||
    experience.error ||
    (needsRecord && recordQuery.isError)
  ) {
    return (
      <ResourceLoadErrorState
        error={
          descriptorQuery.error ??
          (needsRecord ? recordQuery.error : undefined) ??
          experience.error
        }
        onRetry={() => void Promise.all([
          descriptorQuery.refetch(),
          experience.refresh(),
          ...(needsRecord ? [recordQuery.refetch()] : []),
        ])}
      />
    );
  }

  if (!resolved) return <PageNotFoundState />;
  if (resolved.availability !== 'AVAILABLE' && resolved.availability !== 'READ_ONLY') {
    return <PageForbiddenState reasonCode={resolved.reasonCode} decisionId={resolved.decisionId} />;
  }
  if (needsRecord && !recordId) return <PageNotFoundState />;

  const resource = resolved.resource;
  const contract = resolved.forms[mode];
  if (!contract) return <PageNotFoundState />;
  if (mode !== 'view') {
    const mutationRouteId = mode === 'create' ? resource.createRouteId : resource.editRouteId;
    const mutationOperationId = mode === 'create' ? resource.createOperationId : resource.updateOperationId;
    if (!mutationRouteId || !mutationOperationId) return <PageNotFoundState />;
  }

  const backPath = experience.getRoutePath(resource.listRouteId);
  if (!backPath) return <PageNotFoundState />;
  const titleKey = mode === 'create'
    ? resource.createTitleKey ?? resource.listTitleKey
    : mode === 'edit'
      ? resource.editTitleKey ?? resource.listTitleKey
      : resource.detailTitleKey ?? resource.listTitleKey;
  const effectiveInitialValues = initialValues ?? (
    needsRecord ? recordQuery.data : undefined
  );
  const queryInitialValues =
    mode === 'create' && resourceId === 'import-jobs'
      ? (() => {
          const requestedType = new URLSearchParams(window.location.search).get('importType');
          const importType = contract.fields.find(field => field.name === 'importType');
          const supported = new Set(importType?.options?.map(option => String(option.value)) ?? []);
          return requestedType && supported.has(requestedType) ? { importType: requestedType } : undefined;
        })()
      : undefined;
  const resolvedInitialValues: Record<string, unknown> | undefined =
    effectiveInitialValues ?? queryInitialValues;
  if (needsRecord && !resolvedInitialValues) return <PageNotFoundState />;

  const effectiveSuccessPolicy: AdminFormSuccessPolicy = successPolicy ?? (
    resource.detailRouteId
      ? {
          kind: 'navigateToDetail',
          getCreatedId: (result: FormSubmissionResult) =>
            result.returnedValues?.[resolved.table?.rowIdField ?? 'id'] as string | undefined,
        }
      : { kind: 'returnToList' }
  );

  return (
    <AdminFormPage
      resourceId={resourceId}
      mode={mode}
      contract={contract}
      titleKey={titleKey}
      backPath={backPath}
      onSubmit={handleSubmit}
      onAction={onAction}
      initialValues={resolvedInitialValues}
      recordVersion={recordVersion ?? (
        typeof resolvedInitialValues?.['version'] === 'number'
          ? resolvedInitialValues['version']
          : undefined
      )}
      successPolicy={effectiveSuccessPolicy}
      onConflictReload={needsRecord ? async () => {
        // A reload is an explicit operator choice after a 409: replace stale
        // form values with the newly authorized server projection, never retry
        // the mutation with a stale version.
        await recordQuery.refetch();
      } : undefined}
    />
  );
}
